import { z } from 'zod';

export const VIDEO_BUCKET = 'tour-videos';
// Original imported media (including derived posters) rejected by the owner.
const EXCLUDED_VIDEO_PREFIXES = [
  '172d2bcb-574c-4242-8926-e6245b9b4002/import/',
  '172d2bcb-574c-4242-8926-e6245b9b4006/import/',
];
export function hasExcludedVideoMedia(video: { clip_path: string | null; full_path: string | null; poster_path: string | null }) {
  return [video.clip_path, video.full_path, video.poster_path].some(path =>
    path !== null && EXCLUDED_VIDEO_PREFIXES.some(prefix => path.startsWith(prefix)));
}
export const UNCONFIRMED_VIDEO_NAMES = ['por-confirmar-23-manglares-aves'];
export function isIgnoredVideoFilename(filename: string) {
  return filename.toLowerCase().includes('por-confirmar-24-snorkel');
}
export const CONFIRMED_VIDEO_ASSIGNMENTS: Record<string, { productId: string; title: string; destination: string }> = {
  holbox: { productId: '1237869', title: 'Holbox: así se vive', destination: 'Holbox' },
  'holbox-manglares': { productId: '1237871', title: 'Holbox: manglares y playa', destination: 'Holbox' },
  'holbox-yalahau': { productId: '1028954', title: 'Holbox: Ojo de agua Yalahau', destination: 'Holbox' },
  'chichen-valladolid-suytun': { productId: '1237816', title: 'Chichén Itzá y Cenote Suytun', destination: 'Chichén Itzá' },
  'chichen-itza-noche': { productId: '1143198', title: 'Chichén Itzá de noche', destination: 'Chichén Itzá' },
  'tulum-gran-cenote-dos-ojos': { productId: '1240194', title: 'Tulum: Gran Cenote y Dos Ojos', destination: 'Tulum' },
  'tulum-akumal-cenotes': { productId: '1238534', title: 'Tulum y Akumal: cenotes y tortugas', destination: 'Tulum' },
  bacalar: { productId: '1238524', title: 'Bacalar: pontón y rápidos', destination: 'Bacalar' },
  'bacalar-laguna': { productId: '1238529', title: 'Bacalar: laguna y rápidos', destination: 'Bacalar' },
  'las-coloradas': { productId: '1209126', title: 'Las Coloradas y Río Lagartos', destination: 'Las Coloradas' },
  'isla-mujeres-snorkel': { productId: '1237851', title: 'Isla Mujeres: snorkel', destination: 'Isla Mujeres' },
  'tulum-coba-4x1-cenote-playa-del-carmen': { productId: '1238530', title: 'Tulum, Cobá, cenote y Playa del Carmen', destination: 'Tulum / Cobá' },
  'sian-kaan': { productId: '880737', title: "Sian Ka'an", destination: 'Sian Ka’an' },
};
export function confirmedVideoMetadata(filename: string) {
  const name = filename.toLowerCase().replace(/\.(mp4|webm)$/, '');
  return CONFIRMED_VIDEO_ASSIGNMENTS[name === 'por-confirmar-02' ? 'tulum-coba-4x1-cenote-playa-del-carmen' : name] ?? null;
}
export function confirmedVideoProductIds(filename: string) {
  const assignment = confirmedVideoMetadata(filename);
  return assignment ? [assignment.productId] : null;
}
export const VIDEO_SUGGESTIONS = [
  ['Holbox', 'holbox, holbox-manglares, holbox-yalahau'],
  ['Cozumel', 'Completo cozumel-playa-san-martin recibido; clip: primeros 9 segundos del completo'],
  ['Chichén Itzá / Valladolid', 'chichen-itza-valladolid, chichen-valladolid-suytun, chichen-valladolid-xkeken, chichen-itza-noche'],
  ['Tulum / cenotes', 'tulum-gran-cenote-dos-ojos, tulum-akumal-cenotes, tulum-cenote-multun-ha, tulum-playa-del-carmen-casa-tortuga, ruta-de-cenotes'],
  ['Tulum / Cobá · confirmado', 'tulum-coba-4x1-cenote-playa-del-carmen (antes por-confirmar-02) → producto 1238530'],
  ['Sian Ka’an · confirmado', 'sian-kaan → tours de Sian Ka’an del catálogo; encontrado: Oasis Privado (880737). Ignorar por-confirmar-24-snorkel'],
  ['Bacalar', 'bacalar, por-confirmar-05-laguna'], ['Las Coloradas', 'las-coloradas'], ['Isla Mujeres', 'isla-mujeres-snorkel'], ['Isla Contoy', 'Sin video por ahora; no usar clip isla-contoy'],
];
export function isValidFullVideoDuration(duration: number) {
  return Number.isFinite(duration) && duration >= 20 && duration <= 240;
}
export const videoInput = z.object({
  id: z.string().uuid(), title: z.string().trim().min(1).max(160), destination: z.string().trim().min(1).max(100),
  clip_path: z.string().nullable(), full_path: z.string().nullable(), poster_path: z.string().nullable(),
  clip_duration: z.number().min(8.5).max(9.5).nullable(), full_duration: z.number().min(20).max(240).nullable(),
  status: z.enum(['draft', 'published']), sort_order: z.number().int().min(0).max(10000),
  productIds: z.array(z.string().regex(/^\d{1,12}$/)).max(30), confirmed: z.boolean().default(false),
}).superRefine((v, ctx) => {
  if (isIgnoredVideoFilename(v.title)) ctx.addIssue({ code: 'custom', message: 'Ignora el video antiguo; sube sian-kaan.mp4 con el logo corregido.' });
  if (hasExcludedVideoMedia(v)) ctx.addIssue({ code: 'custom', message: 'Estos archivos fueron excluidos por contener marca de agua de otra empresa.' });
  if (v.clip_path && !v.clip_duration) ctx.addIssue({ code: 'custom', message: 'Verifica la duración del clip.' });
  if (v.full_path && !v.full_duration) ctx.addIssue({ code: 'custom', message: 'Verifica la duración del video completo.' });
  if (v.full_duration !== null && !isValidFullVideoDuration(v.full_duration)) ctx.addIssue({ code: 'custom', message: 'El completo debe durar de 20 segundos a 4 minutos.' });
  if (v.status === 'published' && (!v.poster_path || (!v.clip_path && !v.full_path) || !v.productIds.length)) ctx.addIssue({ code: 'custom', message: 'Se requiere portada, video y al menos un tour confirmado.' });
  if (UNCONFIRMED_VIDEO_NAMES.some(n => v.title.toLowerCase().includes(n)) && v.productIds.length && !v.confirmed) ctx.addIssue({ code: 'custom', message: 'Este video necesita confirmación explícita antes de asignarlo.' });
});
export type VideoInput = z.infer<typeof videoInput>;
export type PublicTourVideo = { id: string; title: string; destination: string; clip: string | null; full: string | null; poster: string | null; productIds: string[]; sort_order: number };
const STORY_NAMES: Record<string, string> = {
  '1237869': 'Holbox', '1237871': 'Manglares', '1028954': 'Yalahau',
  '1237816': 'Chichén', '1143198': 'Noche Kukulcán', '1240194': 'Dos Ojos',
  '1238534': 'Akumal', '1238530': 'Cobá', '1238524': 'Bacalar',
  '1238529': 'Laguna', '1209126': 'Coloradas', '1237851': 'Isla Mujeres', '880737': "Sian Ka'an",
};
export function storyCircleName(video: Pick<PublicTourVideo, 'productIds' | 'destination'>) {
  return video.productIds.map(id => STORY_NAMES[id]).find(Boolean) ?? video.destination;
}
export function cacheAgeMinutes(timestamp?: string, now = Date.now()) {
  if (!timestamp) return null;
  const time = Date.parse(timestamp);
  return Number.isFinite(time) && time <= now ? Math.floor((now-time)/60000) : null;
}
export function mayAutoplayVideo(saveData: boolean, reducedMotion: boolean, inView: boolean, visible: boolean) {
  return !saveData && !reducedMotion && inView && visible;
}